import React, { useContext, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useFetch } from 'hooks/use-fetch';
import { SubmitHandler, useForm, Control } from 'react-hook-form';
import Input from 'atomic/form/input';
import Button from 'atomic/button';
import SelectControlled from 'atomic/form/select-controlled';
import Textarea from 'atomic/form/textarea/textarea';
import { useModal } from 'hooks/use-modal';
import { TeApiContext } from '@ecommerce/nest-ssr/common';
import classNames from 'classnames';
import { contactFormData } from 'modules/static-pages/contact-page/components/contact-form/contact-form.data';
import ContactFormModal from 'modules/static-pages/contact-page/components/contact-form-modal';

import './contact-form.scss';

interface SendResponseInterface {
    status: boolean;
    success: boolean;
    message: {
        title: string;
        text: string;
    };
}

interface ContactFormInterface {
    type: string;
    email: string;
    title: string;
    message: string;
}

const ContactForm: React.FC = () => {
    const api = useContext(TeApiContext);

    if (api === null) {
        throw new Error('Invalid API context');
    }

    const sendRecaptcha = useFetch('/contact/recaptcha-verify');
    const sendForm = useFetch('/contact/send');
    const {
        control,
        register,
        handleSubmit,
        reset,
        formState: { errors, isValid },
        getValues
    } = useForm<ContactFormInterface>({ mode: 'onChange' });

    const [sendResponse, setSendResponse] = useState<SendResponseInterface | null>(null);
    const [isRecaptchaValid, setIsRecaptchaValid] = useState(false);
    const sendStatusModal = useModal('SEND_STATUS_MODAL');
    const recaptchaRef = useRef<ReCAPTCHA>(null);
    const options = [
        { value: 'skapiec@skapiec.pl', label: 'Korespondencja ogólna (skapiec@skapiec.pl)' },
        { value: 'uwagi@skapiec.pl', label: 'Uwagi do strony (uwagi@skapiec.pl)' },
        { value: 'partner@skapiec.pl', label: 'Współpraca (partner@skapiec.pl)' },
        { value: 'reklama@skapiec.pl', label: 'Reklama (reklama@skapiec.pl)' },
        { value: 'PR@skapiec.pl', label: 'Informacje prasowe (PR@skapiec.pl)' },
        { value: 'newsletter@skapiec.pl', label: 'Newsletter (newsletter@skapiec.pl)' },
        { value: 'praca@skapiec.pl', label: 'Praca w Skąpiec.pl (praca@skapiec.pl)' },
        { value: 'pp@skapiec.pl', label: 'Program Partnerski (pp@skapiec.pl)' }
    ];

    const onSubmit: SubmitHandler<ContactFormInterface> = data => {
        if (!isRecaptchaValid) {
            return;
        }

        sendForm.post(data).then((res: SendResponseInterface) => {
            if (res.success) {
                reset({
                    type: 'skapiec@skapiec.pl',
                    email: '',
                    title: '',
                    message: ''
                });
            }

            setSendResponse(res);
            sendStatusModal.open();
        });
    };

    const onChange = (): void => {
        sendRecaptcha.post({ token: recaptchaRef.current?.getValue() }).then((res: SendResponseInterface) => {
            setIsRecaptchaValid(res.status);
        });
    };

    return (
        <div
            className={classNames(({
                'contact-form': true,
                'contact-form--mobile': api.getDevice() === 'mobile'
            }))}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <SelectControlled
                    className="contact-form__select"
                    name="type"
                    list={options}
                    label="Rodzaj wiadomości"
                    control={control as unknown as Control}
                    error={errors.type}
                    defaultValue="skapiec@skapiec.pl"
                />
                <Input
                    {...register(
                        'title',
                        {
                            required: contactFormData.errors.requiredMessage
                        }
                    )}
                    small={true}
                    error={errors.title}
                    name="title"
                    label="Temat wiadomości"
                />
                <Input
                    {...register(
                        'email',
                        {
                            required: contactFormData.errors.requiredMessage,
                            pattern: { value: /\S+@\S+\.\S+/, message: contactFormData.errors.patternMessage }
                        }
                    )}
                    small={true}
                    error={errors.email}
                    name="email"
                    label="Twój adres e-mail"
                />
                <Textarea
                    {...register(
                        'message',
                        {
                            required: contactFormData.errors.requiredMessage
                        }
                    )}
                    error={errors.message}
                    name="message"
                    label="Wiadomość"
                    value={getValues('message')}
                />
                <ReCAPTCHA
                    className="recaptcha"
                    ref={recaptchaRef}
                    sitekey="6LerPaAUAAAAABhXge27uFB9UsEfqBOuGX3HTD9N"
                    onChange={onChange}
                />
                <Button
                    type="submit"
                    color="primary"
                    size="lg"
                    disabled={!(isValid && isRecaptchaValid)}
                >
                    Wyślij wiadomość
                </Button>
            </form>
            {sendResponse && sendStatusModal.isOpen && (
                <ContactFormModal
                    onCloseModal={sendStatusModal.close}
                    message={sendResponse?.message}
                    success={sendResponse?.success}
                    isMobile={api.getDevice() === 'mobile'}
                />
            )}
        </div>
    );
};

export default ContactForm;
